import { useMutation, useQuery, UseQueryResult } from '@tanstack/react-query';

import {
  ESelfServeTaskStatus,
  ETaskId,
  SelfServeTask
} from '../common/contracts';
import { EQueryKeys } from '../constants/enums';
import useAxios from '../hooks/useAxios';

import { EApiRoutes, fetchConfig, getApiRouteOrMock } from './api.utils';
import useUsers from './useUsers';

export const useSelfServe = (currentPublisherId?: string | null) => {
  const axios = useAxios();

  const { fetchFeatureFlags } = useUsers({
    publisherId: currentPublisherId || undefined
  });

  const isSelfServeSetupChecklistFeatureFlagOn =
    fetchFeatureFlags?.data?.featureFlags?.dashboard_self_serve_setup_checklist;

  const getTasks: UseQueryResult<SelfServeTask[]> = useQuery({
    ...fetchConfig.general,
    queryKey: [EQueryKeys.SELF_SERVE_TASKS, currentPublisherId],
    queryFn: async () => {
      // todo: remove try/catch after integration
      try {
        if (!currentPublisherId || !isSelfServeSetupChecklistFeatureFlagOn) {
          throw new Error('No publisher ID or feature flag is off');
        }

        const response = await axios.get(
          getApiRouteOrMock(EApiRoutes.SELF_SERVE_TASKS),
          {},
          currentPublisherId
            ? { 'x-publishers': currentPublisherId }
            : undefined
        );

        const tasks = response?.tasks as SelfServeTask[];

        try {
          const testPaymentStatus = tasks.find(
            ({ id }) => id === ETaskId.TEST_PAYMENTS
          )?.status;

          if (testPaymentStatus !== ESelfServeTaskStatus.DONE) {
            const ordersResponse = await axios.get(
              getApiRouteOrMock(EApiRoutes.ORDERS),
              {
                offset: 0,
                recordLimit: 1,
                sortValue: 'date',
                direction: 'desc'
              },
              { 'x-publishers': currentPublisherId }
            );

            const { totalCount } = ordersResponse;

            if (totalCount > 0) {
              updateTask.mutate({
                id: ETaskId.TEST_PAYMENTS,
                status: ESelfServeTaskStatus.DONE
              });
            }
          }
        } catch (error) {
          console.error('Error getting orders for test payment task:', error);
        }

        return tasks;
      } catch (error) {
        console.error('Error fetching tasks', error);
        return [];
      }
    },
    enabled: !!currentPublisherId && isSelfServeSetupChecklistFeatureFlagOn
  });

  const updateTask = useMutation({
    mutationFn: async ({
      id,
      status
    }: {
      id: string;
      status: ESelfServeTaskStatus;
    }) => {
      if (!currentPublisherId) {
        return [];
      }
      const response = await axios.put(
        `${getApiRouteOrMock(EApiRoutes.SELF_SERVE_TASKS)}/${id}`,
        { status },
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
      return response.data;
    }
  });

  const verifyBusiness = useMutation({
    mutationFn: async (data: {
      fullName: string;
      phoneNumber: string;
      email: string;
    }) => {
      const response = await axios.put(
        getApiRouteOrMock(EApiRoutes.SELF_SERVE_VERIFY_BUSINESS),
        data,
        currentPublisherId ? { 'x-publishers': currentPublisherId } : undefined
      );
      return response.data;
    }
  });

  return {
    getTasks,
    updateTask,
    verifyBusiness
  };
};
